import { updatePaginationState } from "@/cache/appstate/WriteQueries";
import { FramerButton, RadioButton, TagItem, TwGlobalFilter } from "@/components/shared";
import { AssetFinancingStatus, GetPaginatedAssetFinancingPoolsQueryVariables } from "@/graphql/__generated__/graphql-operations";
import { usePaginationState } from "@/hooks/cache/appState/usePaginationState";
import { PaginationState } from "@/types";
import { ArrowPathIcon, CheckIcon, FunnelIcon } from "@heroicons/react/24/outline";
import { ChangeEvent, JSX } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useTheme } from "@/context/ThemeProvider";
import { Card } from "@/components/ui/card";

export type propsInvestmentFilterComponent = {
  onFilterSubmit: (_variables: GetPaginatedAssetFinancingPoolsQueryVariables) => void;
  displayAmount: number;
}

type RadioOption = {
  label: string;
  value: AssetFinancingStatus;
}

// Constants
const afpStatusOptions: RadioOption[] = [
  { label: "Open", value: AssetFinancingStatus.Open },
  { label: "Closed", value: AssetFinancingStatus.Closed },
  { label: "Active", value: AssetFinancingStatus.Active },
];

/**
 * InvestmentFilter
 *
 * This component provides filtering functionality for asset financing pools by company name and status.
 *
 * @param {Props} props - The props for the component.
 * @param {string} props.displayAmount - The number of items to display.
 * @param {(_variables: GetPaginatedAssetFinancingPoolsQueryVariables) => void} props.onFilterSubmit - The function to call when the filter is submitted.
 * @returns {JSX.Element} The InvestmentFilter component.
 *
 * @component
 * @example
 * ```tsx
 * return (
 *   <InvestmentFilter onFilterSubmit={handleFilterSubmit} displayAmount={displayAmount} />
 * )
 * ```
 *
 */
function InvestmentFilter(props: propsInvestmentFilterComponent): JSX.Element {

  // Props
  const { onFilterSubmit, displayAmount } = props;

  // Hooks
  const { t } = useTranslation(["translation", "common"]);
  const navigate = useNavigate();
  const paginationState: PaginationState["paginatedAssetFinancingPools"] = usePaginationState("paginatedAssetFinancingPools");
  const { filters: { companyname, status } } = paginationState;
  const { darkMode } = useTheme();

  const backgroundColour = darkMode ? "bg-mfdarklighter text-white border-transparent shadow-gray-900": "bg-white";
  /**
   * OnChange Handler for Filter input field.
   * @param {ChangeEvent<HTMLInputElement>} event - The change event.
   */
  function handleFilterOnChange(event: ChangeEvent<HTMLInputElement>) {
    // event.preventDefault();
    updatePaginationState({
      paginatedAssetFinancingPools: {
        filters: {
          companyname: event.target.value
        }
      }
    });
  }

  /**
   * OnChange handler for Radio buttons
   * @param {string} key
   * @param {string} value
   */
  const onChangeRadioButton = (key: string, value: string) => {
    if (key === "status") {
      updatePaginationState({
        paginatedAssetFinancingPools: {
          filters: {
            status: value as AssetFinancingStatus
          }
        }
      });
    }
  };

  /**
   * OnClick Handler for Reset button.
   */
  function handleResetClick() {
    // Resets the URL
    navigate("/user/investment");
    // Call the filter submit function
    onFilterSubmit({
      first: displayAmount as number,
      status: null,
      companyname: "",
    });
    // Reset TwSelect value
    toast.success(t("common:text.filterremoved"), {toastId: "investmentFilterRemoved"});
  }

  /**
   * OnClick Handler for Submit button.
   */
  function handleSubmitClick() {
    onFilterSubmit({
      first: displayAmount,
      companyname,
      status,
    });
    toast.success(t("common:text.filterapplied"), {toastId: "investmentFilterApplied"});
  }

  return (
    <Card className="mb-4 bg-white px-4 py-3 dark:bg-mfdarklight">
      <div className="flex items-center w-full space-x-2">
        <TwGlobalFilter
          value={companyname ?? ""}
          placeholder={t("investmentpage.filter.placeholder")}
          onChange={handleFilterOnChange}
          className="flex-grow"
        />

        {/* Filters container */}
        <Popover>
          <PopoverTrigger data-cy="investment-filter-popover-trigger" className="bg-transparent">
            {/* Filter box */}
            <Card
              className="flex flex-row item-center bg-transparent h-[36px] md:w-[200px] px-2 py-1 relative select-none shadow-none"
            >
              <div className="sm:mr-2 h-full items-center flex shrink-0">
                <FunnelIcon data-cy="investment-filter-icon" className="w-5 h-5 text-gray-400 dark:text-white" />
                {
                  status === null &&
              <p className="hidden sm:block text-sm text-gray-400 pl-2">
                {t("portfoliopage.filter.selectstatus")}
              </p>
                }
              </div>
              <div className="hidden sm:flex items-center space-x-2 select-none mt-[2px]">
                {
                  status !== null &&
              <TagItem label={status as string} />
                }
              </div>
            </Card>
          </PopoverTrigger>

          <PopoverContent
            data-cy="investment-filter-popover-content"
            align="start"
            className={`shadow-lg h-auto p-4 min-w-[130px] md:min-w-[200px] max-w-min rounded-md dark:text-white mt-1 ${backgroundColour}`}
          >
            {/* Status filters */}
            {afpStatusOptions.map(option => (
              <RadioButton
                key={option.value}
                option={option}
                checked={status === option.value}
                onChange={(value) => onChangeRadioButton("status", value as string)}
              />
            ))}
          </PopoverContent>
        </Popover>

        {/* Action buttons */}
        <div className="flex justify-end items-center gap-2">
          {/* Reset button */}
          <FramerButton
            onClick={handleResetClick}
            variant="outline-primary"
            className="min-w-[50px] lg:min-w-[100px]"
          >
            <ArrowPathIcon className="w-5 h-5 lg:hidden" />
            <div className="hidden lg:block">
              {t("common:buttons.reset")}
            </div>
          </FramerButton>

          {/* Submit button */}
          <FramerButton
            onClick={handleSubmitClick}
            className="bg-mforange text-white min-w-[50px] lg:min-w-[100px] hover:bg-mforange/80"
          >
            <CheckIcon className="w-5 h-5 lg:hidden" />
            <div className="hidden lg:block">
              {t("common:buttons.submit")}
            </div>
          </FramerButton>
        </div>
      </div>
    </Card>
  );
}

export default InvestmentFilter;
